import { Link } from 'react-router';
import { motion } from 'motion/react';
import { Button } from '@mieweb/ui';
import { BrandMark } from '../BrandMark';

interface ComparisonHeroProps {
  competitorName: string;
  competitorEmoji: string;
  competitorCategory: string;
  tagline: string;
}

const fadeUp = { hidden: { opacity: 0, y: 32 }, visible: { opacity: 1, y: 0 } };

export function ComparisonHero({ competitorName, competitorEmoji, competitorCategory, tagline }: ComparisonHeroProps) {
  return (
    <section className="relative overflow-hidden py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white to-orange-50/50 dark:from-amber-950/20 dark:via-neutral-950 dark:to-orange-950/20" />

      <div className="relative max-w-4xl mx-auto text-center">
        {/* Breadcrumb */}
        <motion.nav
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-neutral-400 mb-8"
        >
          <Link to="/" className="hover:text-slate-700 dark:hover:text-neutral-200 transition-colors">Home</Link>
          <span>/</span>
          <Link to="/vs" className="hover:text-slate-700 dark:hover:text-neutral-200 transition-colors">Compare</Link>
          <span>/</span>
          <span className="text-slate-700 dark:text-neutral-200">vs {competitorName}</span>
        </motion.nav>

        {/* VS badge */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="inline-flex items-center gap-3 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-full px-5 py-2 mb-8 shadow-sm"
        >
          <BrandMark className="h-7 w-7" />
          <span className="text-sm font-semibold text-slate-400 dark:text-neutral-500">VS</span>
          <span className="text-2xl">{competitorEmoji}</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
        >
          <span className="text-slate-900 dark:text-white">Pollenate vs </span>
          <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
            {competitorName}
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-lg sm:text-xl text-slate-600 dark:text-neutral-400 max-w-2xl mx-auto mb-4"
        >
          {tagline}
        </motion.p>

        <motion.p
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="text-sm text-slate-500 dark:text-neutral-500 mb-10"
        >
          {competitorCategory}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/login?mode=signup">
            <Button size="lg">Start Free Today</Button>
          </Link>
          <Link to="/docs">
            <Button variant="outline" size="lg">View Documentation</Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
